/**
 * D2 结构化 Canvas 类型(与 ai `longimage/canvas.py` / `style_tokens.py` 逐字段对齐)。
 *
 * styleTokens = 全图唯一视觉源(双驱动 AI 图块 prompt 与 PIL 矢量配色);Canvas 是阶段一
 * (视觉理解)的产出、编辑对象(D3)与阶段二渲染输入(D4)的单一真相源。
 */

export type Material = "matte" | "glossy" | "frosted" | "metallic" | "natural";
export type Lighting = "studio" | "natural" | "dramatic" | "soft";
export type StyleTone =
  | "premium_minimal"
  | "vivid_promo"
  | "warm_life"
  | "tech_cool"
  | "fresh_clean";
export type Density = "compact" | "normal" | "airy";
export type TokenSource = "auto" | "preset" | "manual";
export type AssetRole = "product" | "scene" | "detail" | "logo" | "other";
export type BlockRole =
  | "HERO"
  | "FEATURE"
  | "SPEC"
  | "SCENE"
  | "CTA"
  | "CONTRAST"
  | "TRUST"
  | "PRICE"
  | "INFO"
  | "QR";
/** A 全AI渲染 / B AI皮+矢量叠字 / C 纯矢量。 */
export type RenderMode = "A" | "B" | "C";
export type BlockLayout = "full" | "img_top_text_bottom" | "img_left_text_right" | "two_col";
/**
 * D6 版面主次(与 ai `canvas.py::BlockEmphasis` 逐字段对齐)。
 *
 * hero=最想让人记住的块(画幅提一档)/ minor=过渡补充(降一档)/ normal=按内容量。
 * ai 侧 `a_block_ratio` 消费它换 A 类画幅;图块这边由 `derivePropsFor` 换 blockRatio。
 */
export type BlockEmphasis = "hero" | "normal" | "minor";

// ── 图块画面的五个窄槽(2026-08-02,条 3;与 ai `models.py::ImageSlots` 逐字段对齐)──
/** 镜头形态。9 值,与 ai `AssetShotType` 同一份。 */
export type AssetShotType =
  | "hero"
  | "angle"
  | "detail"
  | "texture"
  | "lifestyle"
  | "flatlay"
  | "inhand"
  | "concept"
  | "other";
/** 拍摄角度。7 值,与 ai `AssetAngle` 同一份。 */
export type AssetAngle = "front" | "side" | "back" | "top" | "45" | "closeup" | "unknown";
/** 留白位置。6 值,与 ai `AssetNegativeSpace` 同一份(**现成枚举,不是本批新造的**)。 */
export type AssetNegativeSpace = "none" | "top" | "bottom" | "left" | "right" | "center";

/**
 * 图块画面的五格 + 一个限定自由口。
 *
 * ★三个选择题(取景 / 光影 / 留白)全部复用**已有**枚举,一个新值都没造 ——
 *   新造有限集就意味着又多一处口径要和 VLM / 规划两边对齐。
 * ★自由口只说「画面里还要有什么」,**不说光 / 材质 / 风格 / 调性**:
 *   那几样各自有槽,说两遍会和整图视觉 DNA 打架(E4② 实测 11% 的块中招,
 *   且违反**全部是属性打架**,不是内容重复)。
 */
export interface ImageSlots {
  subject?: string;
  shotType?: AssetShotType | null;
  angle?: AssetAngle | null;
  environment?: string;
  lighting?: Lighting | null;
  material?: Material | null;
  tone?: StyleTone | null;
  negativeSpace?: AssetNegativeSpace | null;
  extra?: string;
}

/** 五格 + 自由口全空 = 等于没填(消费侧一律走老的自由文本路)。 */
export function isEmptySlots(s: ImageSlots | null | undefined): boolean {
  if (!s) return true;
  return !(
    s.subject?.trim() ||
    s.shotType ||
    s.angle ||
    s.environment?.trim() ||
    s.lighting ||
    s.material ||
    s.tone ||
    s.negativeSpace ||
    s.extra?.trim()
  );
}

export interface StylePalette {
  primary: string;
  secondary: string;
  accent: string;
  ink: string;
  muted: string;
  line: string;
  bg: string;
}

export interface StyleTokens {
  palette: StylePalette;
  material: Material;
  lighting: Lighting;
  tone: StyleTone;
  fontPair: { title: string; body: string };
  density: Density;
  source: TokenSource;
  /**
   * ★整图风格自由段(2026-08-04 导演层批①):styleReco.styleBrief 落进 DNA 的载体,
   * ai 随每个图块 prompt 注入。老草稿没有 → 空串;parse 不收它就死在草稿里(显式重建老病)。
   */
  brief?: string;
  /**
   * ★风格库(一键出图)选中画风的富文本(2026-08-20)。与 brief 是**两个槽**:
   * brief 是「用户自己说的整图气质」80 字上限;这个是我们自己的画风模板文案(几百字)。
   * 挤进 brief 的下场:ai 侧 brief max_length=80,往返校验时炸「至多 80 字」,整单 FAILED
   * (2026-08-20 测试机真浏览器 e2e 实测)。
   */
  stylePreset?: string;
  /**
   * ★用户自己写的视觉/版式要求**原话**(ADV-LI-STYLE-STRENGTH-1,2026-08-21)。
   * 与 brief / stylePreset 是**第三个槽**,来路各不相同:
   *   brief       = 导演/读懂总结出的整图气质(≤80,0816 护栏)
   *   stylePreset = 风格库那 39 条我们自己的模板文案(≤600)
   *   userVisualDirective = 用户自己打进框里的那几句(≤1200)
   * ai 会把它顶到整条出图 prompt 的**第一段**并标「最高优先级」(与单图那条路对齐)。
   * ★`parseStyleTokens` 不收它 = 用户写的画面要求**存一次草稿就没了**,而且一声不响。
   */
  userVisualDirective?: string;
  /**
   * ★这套色是不是用户自己写的(ADV-LI-STYLE-STRENGTH-1)。
   * 为 true 时 ai 的 `prompt_dna` 收尾句改口(从「所有区块统一使用这套配色」
   * 改成「这套配色取自用户原话」)。
   * ★为什么不塞进 `source`:`TokenSource` 是三值闭集,多一个值 → parseStyleTokens
   *   整份丢弃按「没有配色」处理,用户的配色**静默消失**。
   */
  paletteFromUser?: boolean;
  /**
   * ★**只属于某一段**的视觉要求(LI-ROUTE-1,2026-08-22)。
   * 老板 0821 走查:「我看你只是把用户要求都放到一个里面了好像」——
   * 实查真 prompt:6 个图块的【用户要求】段**逐块 md5 相同**,首屏那块被告知
   * 「第四部分要以统一科技图标矩阵展示 18 个功能舱」。
   * ⇒ 拆两格:`userVisualDirective` 只留**整图级**的(配色/字体,每块都发),
   *   段级的落这里,按 `outlineIndex` 只发给对应那一块。
   * ★`outlineIndex` 是**用户大纲的下标**,与 `CanvasBlock.outlineIndex`、
   *   `LongImageIntentItem.outlineIndex` **同一把尺**。不同尺不报错,只是送错块。
   */
  sectionDirectives?: Array<{ outlineIndex: number; text: string }>;
}

/** 安全预设(= ai safe_default_style_tokens;与旧版 build_palette("white") 同色系)。 */
export function safeDefaultStyleTokens(): StyleTokens {
  return {
    palette: {
      primary: "#4F46E5",
      secondary: "#F7F8FA",
      accent: "#4F46E5",
      ink: "#171A2D",
      muted: "#646A82",
      line: "#E0E3EC",
      bg: "#FFFFFF",
    },
    material: "matte",
    lighting: "studio",
    tone: "premium_minimal",
    fontPair: { title: "modern_sans", body: "modern_sans" },
    density: "normal",
    source: "auto",
  };
}

export interface CanvasSellingPoint {
  id: string;
  text: string;
  group: string;
}

export interface CanvasProductSpec {
  label: string;
  value: string;
  unit: string;
}

export interface CanvasProduct {
  name: string;
  category: string;
  brand: string;
  sellingPoints: CanvasSellingPoint[];
  specs: CanvasProductSpec[];
  price: { now: string; old: string; currency: string };
  compliance: { category: string; riskWords: string[] };
}

export interface CanvasAsset {
  objectKey: string;
  role: AssetRole;
  ratio: string;
  w?: number | null;
  h?: number | null;
  understood?: {
    subject: string;
    background: string;
    dominantColors: string[];
  } | null;
}

export interface CanvasDataPoint {
  label: string;
  value: string;
  unit: string;
  /** product.specs 下标绑定 → 改产品信息数据块联动;null=无绑定。 */
  bindTo?: number | null;
}

export interface CanvasBlockContent {
  title: string;
  subtitle: string;
  bullets: Array<{ text: string }>;
  dataPoints: CanvasDataPoint[];
  cta: string;
}

export interface CanvasBlockImage {
  objectKey: string;
  role: AssetRole;
  ratio: string;
  /** D1 保真通路参数:enabled 时该图走 image_edit(gpt-image-1 + input_fidelity)。 */
  i2i: { enabled: boolean; fidelity: "high" | "low"; maskHint: string };
  /**
   * 2026-07-30:VLM 看图那一刻的两条出图事实,由 ai 侧随图下发。
   * 老草稿没这两个字段 → 消费侧一律给默认值(`?? false` / `?? "none"`)。
   */
  hasText?: boolean;
  textSafe?: "none" | "top" | "bottom" | "left" | "right" | "center";
}

export interface CanvasBlockGen {
  provider: string;
  model: string;
  tier: string;
  seed: string;
  promptUsed: string;
  assetSeq?: number | null;
  status: "PENDING" | "RUNNING" | "SUCCESS" | "FAILED" | "";
  failureReason: string;
}

/**
 * ★排屏归属标注(LI-SCREENS-2.0 WP5,2026-09-05;镜像 ai
 * `app/modules/poster/longimage/models.py::BlockScreenAnno`,wire 字段名 `screenAnno`
 * 与 ai 侧 `Field(alias="screenAnno")` 逐字同名 —— TS 侧无需再起别名)。
 * 排屏规划器(一键/智能稿两 lane)产物随 Canvas 带下来;手工搭建的块没有这一格
 * (`undefined`/`null` = 无标注,装箱交给渲染层现役 `plan_screens` 兜底,行为逐字不变)。
 */
export interface BlockScreenAnno {
  /** 属于第几屏(1 起),与 ai `ScreenPlan.screens[].no` 同尺。 */
  screenNo: number;
  /** 屏角色:cover/value/spec/scene/evidence/cta(闭集校验在 ai 规划层,这里宽收)。 */
  screenRole: string;
  /** 本屏画面/构图/配图意图(规划器产出,只读展示用)。 */
  screenVisual?: string;
  /**
   * ★屏画幅手选(LI30-C1,0910 拍板⑤)。缺省/null = 智能(ai 按密度自动);
   * 手选后写到该屏**全部块**的标注上(ai 读第一块,同屏同值)。与 ai
   * `BlockScreenAnno.screen_ratio` 同批成对 —— 单边发会被 ai 严校验拒。
   */
  screenRatio?: ScreenRatio | null;
  /** 规划器当初按密度排定的画幅(ai `planned_ratio`,只读回传;改稿带回时原样还给 ai)。 */
  plannedRatio?: ScreenRatio | null;
}

/** 屏/块画幅五档(与商品主图 PosterRatio、ai 白名单同集)。 */
export type ScreenRatio = "3:4" | "1:1" | "16:9" | "4:5" | "9:16";

export interface CanvasBlock {
  blockId: string;
  order: number;
  role: BlockRole;
  /** 视觉模板 = 现有 33 样式库 key(STYLE_META 白名单)。 */
  styleTag: string;
  renderMode: RenderMode;
  layout: BlockLayout;
  /** 老草稿/老后端可能缺 → 消费侧按 "normal" 兜底。 */
  emphasis?: BlockEmphasis;
  /**
   * ★这一块实现的是**用户大纲第几条**(-1 / 缺 = 整图级)。C1,LI-ROUTE-1。
   * 出图时按它过滤「只属于某一段」的用户要求与逐字文案。
   * ★为什么落在块上、而不是出图时现查:它的唯一来源是 `ContentPlan.sections[i].fromOutline`,
   *   而 **ContentPlan 从来没上过线**(投出去的是 Canvas,里面没有 plan)——
   *   跳 3 的产物到跳 4 就没了,不带在身上下游就是**算不出来**。
   */
  outlineIndex?: number;
  content: CanvasBlockContent;
  images: CanvasBlockImage[];
  styleOverride?: { accent?: string | null; bg?: string | null; tone?: StyleTone | null } | null;
  /** 功效词降级映射(raw→safe),可审计。 */
  compliance: Array<{ raw: string; safe: string }>;
  gen?: CanvasBlockGen | null;
  /**
   * ★图块画面的窄槽(2026-08-02,条 3)。缺 = 规划没给 / 非图块 / 老稿 → 走老的自由文本路。
   *
   * 它是**图块 prompt 的新来源**:在它之前,图块 prompt 由 `derivePropsFor` 把
   * title+subtitle+bullets 拼起来,也就是「57% 的画面描述其实是文案」的真身。
   */
  scene?: ImageSlots | null;
  /**
   * ★卖点短语上图(2026-08-04 导演层批② T1):图块画面上主标题之外的 1~3 条短语,
   * ai 解析层已过逐字门(用户语料核到才留)。缺 = 老稿/非图块 → 不叠短语。
   * `derivePropsFor` 图块分支透传为 props.overlayLines(ai `ImageProps.overlayLines`)。
   */
  overlayLines?: string[];
  /**
   * ★这块的光影/质感/调性还跟着整图风格走吗(2026-08-03 批次③)。
   * true = 由 styleReco 派生 → 换画风时确定性重推;缺/false = 手改过或老稿 → 不动
   * (「默认补排不覆盖」同原则:宁可少派生一块,不许覆写用户改过的字)。
   */
  styleDerived?: boolean;
  /**
   * ★排屏归属标注(LI-SCREENS-2.0 WP5)。缺省(`undefined`/`null`)= 无标注
   * (存量稿/手工搭建器)—— 行为逐字不变,详见 {@link BlockScreenAnno} 头注。
   */
  screenAnno?: BlockScreenAnno | null;
  /**
   * ★原件屏专用槽(LI-SCREENS-2.0,镜像 ai `LongImageBlock.originals`,
   * wire 字段名 `originalKeys` 逐字同名)。资质/证书/奖牌图 objectKey 列表 ——
   * 非空 = 该块并入零 AI 重绘的原件屏(设计定稿§0①:证书文字交 AI 必被篡改,合规红线),
   * 与 `ENGINE_FREE_IMAGE_TAGS`(photocards)同一价值观、不同触发条件。
   * 空(缺省,全部存量稿)= 非原件块,行为逐字不变。
   */
  originalKeys?: string[];
}

export interface CanvasMeta {
  targetWidth: number;
  scale: 1 | 2 | 3;
  direction: "v" | "h";
  useCase: string;
  estHeight?: number | null;
  maxHeight?: number | null;
}

/**
 * 一条事实的出处(与 ai `canvas.py::Provenance` 逐字段对齐)。
 *
 * ★定义放在 canvas.ts 而不是 comprehend.ts,与 ai 侧同构:出处**是 Canvas 的一部分**,
 *   不是读懂那一跳的临时产物。`comprehend.ts` 从这里 re-export,全仓只有一份定义。
 */
export interface ProvenanceEntry {
  /** 字段路径;数组带下标 `sellingPoints[0]`,真跑里模型会给更细的 `specs[0].label` */
  field: string;
  /** 它在原文里**原样**的样子 */
  value: string;
  /** `text` / `image:<objectKey>` / `product_lib` / `inferred` */
  from: string;
}

/** 意图单里的一条 —— 逐字原文 + 它来自用户写的第几行(与 ai `canvas.IntentItem` 同形)。 */
export interface LongImageIntentItem {
  text: string;
  srcLine: number;
  /**
   * ★这一条落在**用户大纲第几条**(-1 = 整图级,发给每一块)。LI-ROUTE-1。
   * 与 `CanvasBlock.outlineIndex`、`StyleTokens.sectionDirectives[].outlineIndex`
   * **同一把尺**(用户大纲的下标,**不是标题所在的行号**)。
   * ★下标与行号同为小整数,用错了不报错、不崩,只是所有文案**整体错位一格**,
   *   而且只在有大纲的单上才看得出来。
   */
  outlineIndex?: number;
}
/** 用户意图单(与 ai `canvas.UserIntent` 同形;线上字段名就是 copy / taboo)。 */
export interface LongImageUserIntent {
  copy?: LongImageIntentItem[];
  taboo?: LongImageIntentItem[];
}

export interface LongImageCanvas {
  version: 2;
  product: CanvasProduct;
  assets: CanvasAsset[];
  styleTokens: StyleTokens;
  blocks: CanvasBlock[];
  canvasMeta: CanvasMeta;
  /**
   * ★每条事实的出处(2026-08-01 随 canvas 落库)。
   *
   * 老草稿 / 老路(模板·蓝图·场景卡·横宽)进来的稿没有这一项 → `undefined`。
   * **空与"来源不明"是两件事**:没读懂过就不画徽章,不是画一个"来源不明"。
   */
  provenance?: ProvenanceEntry[];
  /**
   * ★骨架稿(2026-08-05,E2 拍板):零事实+零图+零画面意图,由 ai 规划判定。
   * 出图时图块不下引擎(示意位占位,不计费)。老草稿没有这一项 → undefined。
   */
  skeleton?: boolean;
  /**
   * ★数字白名单的锚点:只有原文(用户原文 + 图上 OCR 原文 + 产品库原文)。
   *
   * 块级改写拿它判「这个数字是不是编的」。此前没落库,前端只能退而用产品事实当锚
   * (见 `FactRail.factsOf` 那段注释里记的欠账)—— 现在换回真锚。
   */
  rawBlob?: string;
  /**
   * ★用户意图单里今天没有消费方的两格(ADV-LI-COPY-TABOO-1,2026-08-21)。
   *   `copy` = 引号里的成品文案(原样印);`taboo` = 忌讳项(落出图 prompt 的否定段)。
   *   每条带 `srcLine`,回执要能说「这来自你写的第 N 句」。
   *   缺 = 老稿/老路 ——   **不是**"用户没写"(与 provenance 缺席同口径)。
   */
  userIntent?: LongImageUserIntent;
  /**
   * ★「用已有料排这段」的回执(2026-08-03 缺口三选一)。只在带 aiFillSections 的
   * 重排响应里非空;补料台据此渲染诚实回执(「料里确实没有 XX,我不会替你编」)。
   * 页面级一次性反馈,消费方是页面 state,不读草稿里的存量值。
   */
  aiFillReceipts?: AiFillReceipt[];
  /** ★整图风格单一来源(2026-08-03 批次③)。缺 = 老稿/老路 → 不画推荐 chip。 */
  styleReco?: StyleReco | null;
  /**
   * ★统一料单三态账(2026-08-17,老板「料单统一 + 落选回显」)。每条料可追踪到
   * 已编入/兜底接住/落选 —— 消除产品库料的静默落选。缺 = 老稿/存量入口(没传料单)→ 不画回显。
   * 对侧 ai `v2/material_ledger.LedgerEntry`。
   */
  materialLedger?: MaterialLedgerEntry[];
  /**
   * ★每屏出图提示词只读预览(LI30-PV,2026-09-12):ai `plan_build` 随编排产物带回,
   * 与渲染层 compose 同源(不含画幅/参考图等渲染期系统指令)。缺 = 老产物/预拼失败
   * → 屏卡不画「查看出图提示词」入口。只进页面 state 展示,**不回送**任何提交契约
   * (canvasBridge 不读它,buildCanvasCreatePayload 不发它)。
   */
  screenPromptPreviews?: Array<{ screenNo: number; ratio?: string; prompt: string }>;
  /**
   * ★排屏管线对账(LI30,2026-09-10 拍板④降级可见):ai `plan_build` 随产物带回。
   * `degraded`=排屏用了备用模型(主力挂,同管线换档);`fallbackUsed` 非空=整条回落
   * 老兜底管线(质量口径不同,建议重排)。缺 = 老稿/回落产物没带 → 不画提示。
   */
  screenPlanMeta?: {
    plannerModel?: string | null;
    degraded?: boolean;
    fallbackUsed?: string[];
    /**
     * 屏卡 P1(0910 拍板):排屏质检 soft 残留(重排一轮仍在,放行出图,警告留痕)。
     * code: conservation(承诺条数)/budget(行数预算)/near_dup/uniqueness;
     * screenNo 在场时可精确指屏。ai screen_lints.LintViolation 镜像(by_alias)。
     */
    lintWarnings?: Array<{
      code: string;
      screenNo?: number | null;
      message: string;
      evidence?: string[];
    }>;
  } | null;
}

/** 料单三态记账条目(ai v2/material_ledger.LedgerEntry 镜像)。 */
export interface MaterialLedgerEntry {
  id: string;
  text: string;
  source: "product_lib" | "hand_fill" | "text" | "image";
  state: "composed" | "backfilled" | "rejected";
  /** composed 时:落在哪个 blockId(可点跳转)。 */
  placement?: string | null;
  /** rejected 时:落选理由(未被抽取识别 / 版面预算不足)。 */
  reason?: string | null;
}

/**
 * P2 编排异步化:plan 作业状态(通用形状,与视频编排线共用)。QUEUED→RUNNING→DONE/FAILED。
 * canvas 产物不在此(回写草稿 draftMeta.plan.result,经 getDraft 的 planResultJson 拿)。
 */
export type LongImagePlanStatus = "QUEUED" | "RUNNING" | "DONE" | "FAILED";

export interface LongImagePlanJob {
  jobId: number;
  draftId: number;
  jobType: string;
  status: LongImagePlanStatus;
  reason?: string | null;
  actionId?: string | null;
}

/**
 * 整图风格单一来源(ai canvas.StyleReco 的镜像,2026-08-03 批次③)。
 * plan 按用途+料推测的整图基调;各块光影/质感/调性从它派生。
 * 缺 = 老稿/老路/规划兜底 —— 不画推荐 chip(没判过≠判不出)。
 */
export interface StyleReco {
  tone: StyleTone;
  lighting: Lighting;
  material: Material;
  /** 「B2B 通讯服务商,科技感更专业」—— 推荐 chip 上给用户看的那句 */
  reason: string;
}

/** 一条「用已有料排这段」的回执(ai canvas.AiFillReceipt 的镜像)。 */
export interface AiFillReceipt {
  /** 缺口 key(specs / trust,与 Gap.key 同一套词)。 */
  key: string;
  /** 段名词面(「业务数据 / 发展历程」)。 */
  label: string;
  /** true = 重挖出了料且这版真排出了段;false = 没料或没排成,缺口保持开。 */
  filled: boolean;
  message: string;
}

/** 阶段一入参(backend /poster/longimage/canvas/plan;objectKey 由 backend 兑换 presigned url)。 */
export interface CanvasPlanRequest {
  useCase?: string;
  targetWidth?: number;
  scale?: 1 | 2 | 3;
  images: Array<{ objectKey: string; ratio?: string }>;
  product: {
    name?: string;
    category?: string;
    brand?: string;
    sellingPoints?: string[];
    specs?: CanvasProductSpec[];
    specsText?: string;
    priceNow?: string;
    priceOld?: string;
    complianceCategory?: string;
  };
  notes?: string;
  /**
   * 2026-07-31 采集重构:读懂那一跳的产物原样回传。
   *
   * ★带了 ai 侧就**跳过 VLM**(assets/styleTokens 复用)、数字白名单改锚 rawBlob;
   *   不带 = 走老路(模板 / 蓝图 / 场景卡 / 横宽四条存量入口零回归)。
   */
  comprehended?: unknown;
  /**
   * ★「用已有料排这段」的委托(2026-08-03 缺口三选一):缺口 key(specs/trust)。
   * ai 侧先对料台定向重挖再规划,回执落 `LongImageCanvas.aiFillReceipts`。
   */
  aiFillSections?: string[];
  /**
   * ★前置风格意向(2026-08-03 批次③第 3 步):「想要的感觉(可选)」chips,
   * 值 = StyleTone 枚举 key。给了 plan 照办不推测(ai 侧硬约束);空 = AI 定。
   */
  styleHint?: string;
  /**
   * ★追爆双通道(LI-SCREENS-2.0 WP5,设计定稿§3):蓝图 handoff 映射产物,
   * 字段名与 ai `structure_ref.StructureRef`(alias)逐字同名 —— 见
   * `blueprint.ts::structureRefFromHandoff`。undefined = 非蓝图来路
   * (用户自己打字/军师直达),存量行为一字不变。
   */
  structureRef?: {
    blueprintId: number;
    formulaName: string;
    sourcePlatform?: string;
    breakdown: string;
    explosivePoints?: string[];
  };
  /**
   * ★报价屏数区间下沿(LI-SCREENS-2.0 WP5,塌缩护栏屏数尺):只在**一键路**提交时给,
   * 值取自按钮上展示的那个下沿(`longImageOneClickEstimate(...).screensLow`)——
   * 用户按着这个数点头,ai `screen_lints.collapse_lint` 据此判有没有塌缩。
   */
  promisedScreensLow?: number;
}
